import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    TextField,
    Grid
} from '@mui/material';
import TeamModal from 'team/shared/components/TeamModal';
import TeamDarkButtonWithIcon from 'team/shared/components/button/TeamDarkButtonWithIcon';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';

const TeamPronounModal = ({ open, onClose, currentPronoun, onSave }) => {
    // Default to current pronoun or "he" as fallback
    const [selectedPronoun, setSelectedPronoun] = useState(currentPronoun || 'he');
    // State for custom pronoun text
    const [customPronounText, setCustomPronounText] = useState('');

    // Initialize correctly when modal opens
    useEffect(() => {
        if (open) {
            // Check if current pronoun is one of the standard options
            if (currentPronoun && !['he', 'she', 'they', 'ze', '', 'custom'].includes(currentPronoun)) {
                // It's a custom pronoun
                setSelectedPronoun('custom');
                setCustomPronounText(currentPronoun);
            } else {
                // It's a standard pronoun
                setSelectedPronoun(currentPronoun || 'he');
                setCustomPronounText('');
            }
        }
    }, [currentPronoun, open]);

    const handleChange = (event) => {
        setSelectedPronoun(event.target.value);
    };

    const handleSave = () => {
        // If custom is selected, save the custom text instead of 'custom'
        if (selectedPronoun === 'custom' && customPronounText.trim()) {
            onSave(customPronounText.trim());
        } else {
            onSave(selectedPronoun);
        }
        onClose();
    };

    // Define pronoun options
    const pronounOptions = [
        { value: 'he', label: 'He/Him/His' },
        { value: 'she', label: 'She/Her/Hers' },
        { value: 'they', label: 'They/Them/Theirs' },
        { value: 'ze', label: 'Ze/Zir/Zirs' },
        { value: '', label: 'Prefer not to specify' }
    ];

    return (
        <TeamModal
            open={open}
            onClose={onClose}
            width="450px"
            height="auto"
        >
            <Box sx={{ padding: 2 }}>
                <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                    Edit Pronouns
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Adding your pronouns to your card will show them beside your name, leaving the field empty will leave this blank.
                </Typography>

                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <RadioGroup
                        aria-label="pronouns"
                        name="pronouns"
                        value={selectedPronoun}
                        onChange={handleChange}
                    >
                        {/* Standard pronoun options */}
                        {pronounOptions.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio />}
                                label={option.label}
                                sx={{
                                    marginY: 1,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.9rem'
                                    }
                                }}
                            />
                        ))}

                        {/* Custom option with text field inline */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginY: 1,
                                borderRadius: 1,
                                padding: selectedPronoun === 'custom' ? 1 : 0,
                                backgroundColor: selectedPronoun === 'custom' ? '#f5f5f5' : 'transparent',
                                transition: 'all 0.2s ease-in-out',
                                border: selectedPronoun === 'custom' ? '1px solid #e0e0e0' : '1px solid transparent'
                            }}
                        >
                            <FormControlLabel
                                value="custom"
                                control={<Radio />}
                                label="Custom"
                                sx={{
                                    marginRight: 1,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.9rem'
                                    }
                                }}
                            />

                            {/* Custom text field is right next to the label */}
                            {selectedPronoun === 'custom' && (
                                <TextField
                                    size="small"
                                    placeholder="Enter custom pronouns"
                                    value={customPronounText}
                                    onChange={(e) => setCustomPronounText(e.target.value)}
                                    autoFocus
                                    variant="outlined"
                                    sx={{
                                        flex: 1,
                                        mr: 1
                                    }}
                                    InputProps={{
                                        sx: {
                                            backgroundColor: '#ffffff',
                                        }
                                    }}
                                />
                            )}
                        </Box>
                    </RadioGroup>
                </FormControl>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{
                            textTransform: 'none',
                            borderColor: '#dedede',
                            color: '#212121',
                            borderRadius: '20px',
                            padding: '8px 16px',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#f7f7f7',
                                borderColor: '#dedede',
                            }
                        }}
                    >
                        Dismiss
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={selectedPronoun === 'custom' && !customPronounText.trim()}
                        sx={{
                            backgroundColor: '#000000',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: '20px',
                            padding: '8px 16px',
                            '&:hover': {
                                backgroundColor: '#333333',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#cccccc',
                                color: '#666666'
                            }
                        }}
                    >
                        Save Pronouns
                    </Button>
                </Box>
            </Box>
        </TeamModal>
    );
};

export default TeamPronounModal;